/**
 * Panel Width Utilities
 *
 * Manages resizable panel widths with localStorage persistence, keyed by a
 * PanelWidthConfig so multiple independent panels (the chat panel, the
 * Workflow Builder's list pane, its build-agent chat pane, and so on) can
 * each read, clamp and persist their own width without colliding in storage.
 *
 * Originally extracted from Chat.tsx for testability and reusability, then
 * generalised for the Workflow Builder (LSEG-91) so every resizable panel
 * in the app shares the same read/clamp/persist mechanics.
 */

import { logger } from "@/telemetry";

/** Configuration describing a single resizable panel's storage key and bounds. */
export interface PanelWidthConfig {
  /** localStorage key the width is persisted under. */
  storageKey: string;
  /** Width to fall back to when nothing valid is stored. */
  defaultWidth: number;
  /** Minimum allowed width. */
  minWidth: number;
  /** Maximum allowed width. */
  maxWidth: number;
}

/**
 * Get the initial width for a panel from localStorage.
 * Returns the config's default width if no valid value is saved.
 */
export function getInitialPanelWidth(config: PanelWidthConfig): number {
  try {
    const saved = localStorage.getItem(config.storageKey);

    if (saved) {
      const width = parseFloat(saved);

      // Validate the saved width is within the configured range
      if (width >= config.minWidth && width <= config.maxWidth) {
        return width;
      }
    }
  } catch (error) {
    logger.warn("Failed to load saved panel width:", { error: String(error) });
  }

  return config.defaultWidth;
}

/**
 * Save a panel's width to localStorage under its configured key.
 */
export function savePanelWidth(config: PanelWidthConfig, width: number): void {
  try {
    localStorage.setItem(config.storageKey, width.toString());
  } catch (error) {
    logger.warn("Failed to save panel width:", { error: String(error) });
  }
}

/**
 * Constrain a width value to a panel's configured valid range.
 */
export function constrainPanelWidth(config: PanelWidthConfig, width: number): number {
  return Math.max(config.minWidth, Math.min(config.maxWidth, width));
}

// ─── Chat panel (legacy percentage-based width) ─────────────────────────────
//
// These exports predate PanelWidthConfig and are kept for every existing
// consumer (Chat.tsx, usePersistentResizablePanel.ts). They are now thin
// wrappers over CHAT_PANEL_CONFIG so behaviour is unchanged.

export const CHAT_PANEL_WIDTH_KEY = "chatPanelWidth";
export const DEFAULT_CHAT_WIDTH = 65;
export const MIN_CHAT_WIDTH = 20;
export const MAX_CHAT_WIDTH = 75;

/** Config backing the chat panel wrapper functions below. */
export const CHAT_PANEL_CONFIG: PanelWidthConfig = {
  storageKey: CHAT_PANEL_WIDTH_KEY,
  defaultWidth: DEFAULT_CHAT_WIDTH,
  minWidth: MIN_CHAT_WIDTH,
  maxWidth: MAX_CHAT_WIDTH,
};

/**
 * Get the initial chat panel width from localStorage.
 * Returns the default width if no valid value is saved.
 */
export function getInitialChatWidth(): number {
  return getInitialPanelWidth(CHAT_PANEL_CONFIG);
}

/**
 * Save the chat panel width to localStorage.
 */
export function saveChatWidth(width: number): void {
  savePanelWidth(CHAT_PANEL_CONFIG, width);
}

/**
 * Constrain a width value to the valid range.
 */
export function constrainWidth(width: number): number {
  return constrainPanelWidth(CHAT_PANEL_CONFIG, width);
}

// ─── Workflow Builder panes (pixel-based widths) ────────────────────────────
//
// The Workflow Builder's three-pane grid persists the list pane and
// build-agent chat pane widths in pixels (unlike the chat panel above,
// which is a percentage of its container). See usePersistentResizablePane.ts.

/** List pane (workflow list) width config for the Workflow Builder. */
export const BUILDER_LIST_PANE_CONFIG: PanelWidthConfig = {
  storageKey: "pathway.workflowBuilder.listPaneWidth",
  defaultWidth: 250,
  minWidth: 180,
  maxWidth: 420,
};

/** Build-agent chat pane width config for the Workflow Builder. */
export const BUILDER_CHAT_PANE_CONFIG: PanelWidthConfig = {
  storageKey: "pathway.workflowBuilder.chatPaneWidth",
  defaultWidth: 380,
  minWidth: 280,
  maxWidth: 620,
};
